import { useEffect } from 'react';
import type { FieldErrors } from 'react-hook-form';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import type { OsobyAdresaDto } from '@gov-nx/api';
import { pipe } from '@gov-nx/core/types';
import {
	usePersonStoreCommunication,
	usePersonStoreIndividual,
} from '@gov-nx/store';
import type { IUseBoolean } from '@gov-nx/utils/common';
import { getDefaultPhoneValue, useBoolean } from '@gov-nx/utils/common';
import type { ContactDetailsFormData } from '../Contacts/useContactDetailsFormDefinition';
import type { useCountryCodes } from '../Phone/useCountryCodes';

export interface PersonCommunicationData {
	emailVerified?: string;
	unverifiedEmail?: string;

	phoneVerified?: string;
	unverifiedPhone?: string;

	address?: OsobyAdresaDto;

	control: {
		isEditingEmail: IUseBoolean;
		isEditingPhone: IUseBoolean;
		isEditingAddress: IUseBoolean;
	};

	onFormValidated: <T extends ContactDetailsFormData>(
		errors: FieldErrors<T>
	) => void;
}

/**
 * Provides person communication state and validation handlers.
 *
 * Note: to populate the form with initial email/phone values, use
 * `usePersonCommunicationForm(...)` after the form has been defined.
 * `usePersonCommunicationForm` is responsible for writing those values into
 * the form instance.
 */
export const usePersonCommunicationData = (): PersonCommunicationData => {
	const { emailVerified, unverifiedEmail, phoneVerified, unverifiedPhone } =
		usePersonStoreCommunication();
	const { address } = usePersonStoreIndividual();

	const isEditingEmail = useBoolean();
	const isEditingPhone = useBoolean();
	const isEditingAddress = useBoolean();

	return {
		emailVerified,
		phoneVerified,
		unverifiedEmail,
		unverifiedPhone,
		address,
		control: {
			isEditingEmail,
			isEditingPhone,
			isEditingAddress,
		},
		onFormValidated: (errors) => {
			if (errors.phone) {
				isEditingPhone.setTrue();
			}
			if (errors.email) {
				isEditingEmail.setTrue();
			}
		},
	};
};

export const usePersonCommunicationForm = <T extends ContactDetailsFormData>(
	form: FormDefinition<T>,
	{
		countryCodes,
		phone,
		email,
	}: {
		countryCodes: ReturnType<typeof useCountryCodes>;
		email?: string;
		phone?: string;
	}
) => {
	const valuesSet = useBoolean();

	const { formMethods } =
		form as unknown as FormDefinition<ContactDetailsFormData>;

	useEffect(() => {
		if (valuesSet.isFalse && countryCodes.isFetched) {
			formMethods.setValue('_useEmail', !!email);
			if (email) {
				formMethods.setValue('email', email);
			}

			formMethods.setValue('_usePhone', !!phone);
			if (phone) {
				formMethods.setValue(
					'phone',
					pipe(
						phone,
						countryCodes.stringToPhoneShape,
						(shape) => shape ?? getDefaultPhoneValue()
					)
				);
			}

			valuesSet.setTrue();
		}
	}, [valuesSet, formMethods, countryCodes, email, phone]);
};
